import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { 
  BarChart, 
  Bar, 
  XAxis, 
  YAxis, 
  CartesianGrid, 
  Tooltip, 
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  Legend,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis
} from 'recharts';
import { 
  ArrowLeft, 
  BarChart3,
  Home,
  Target,
  Users,
  ClipboardList,
  TrendingUp,
  Trophy,
  User,
  Calendar,
  Zap,
  Shield,
  Hand
} from "lucide-react";

const COLORS = ['#007AFF', '#CCFF00', '#EF4444', '#22C55E', '#A855F7', '#F59E0B'];

// Chart style constants (extracted to avoid inline object re-creation)
const CHART_TICK_STYLE = { fill: '#a1a1aa' };
const CHART_TICK_SMALL = { fill: '#a1a1aa', fontSize: 12 };
const CHART_TOOLTIP_STYLE = { backgroundColor: '#1a1a1a', border: '1px solid #333' };
const CHART_LABEL_STYLE = { color: '#fff' };
const CHART_DOMAIN = [0, 10];
const BAR_RADIUS = [0, 4, 4, 0];

export default function StatisticsPage({ evaluator }) {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    fetchStatistics();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchStatistics = async () => {
    try {
      const response = await axios.get(`${API}/statistics/dashboard`);
      setStats(response.data);
    } catch (error) {
      toast.error("Erro ao carregar estatísticas");
    } finally {
      setIsLoading(false);
    }
  };

  const getScoreColor = (score) => {
    if (score >= 8) return "text-green-400";
    if (score >= 5) return "text-yellow-400";
    return "text-red-400";
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit' });
  };

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <p className="text-muted-foreground">Carregando estatísticas...</p>
      </div>
    );
  }

  const positionData = stats?.position_distribution 
    ? Object.entries(stats.position_distribution).map(([name, value]) => ({ name, value }))
    : [];

  const skillChartData = stats?.skill_statistics?.map(s => ({
    skill: s.skill,
    média: s.average,
    máximo: s.max_value,
    mínimo: s.min_value
  })) || [];

  const radarData = stats?.skill_statistics?.map(s => ({
    skill: s.skill,
    value: s.average
  })) || [];

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border/50 bg-card/50 backdrop-blur-sm sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={() => navigate('/dashboard')}
              data-testid="back-btn"
            >
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-xl md:text-2xl font-bold tracking-tight">
                Estatísticas Avançadas
              </h1>
              <p className="text-sm text-muted-foreground">
                Análise detalhada do desempenho
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Main Content */}
      <main className="container mx-auto px-4 py-8 content-with-nav">
        {/* Overview Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <Card className="bg-card border-border/50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Jogadores</p>
                  <p className="text-3xl font-black">{stats?.total_players || 0}</p>
                </div>
                <Users className="h-8 w-8 text-primary opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card border-border/50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Avaliações</p>
                  <p className="text-3xl font-black">{stats?.total_evaluations || 0}</p>
                </div>
                <ClipboardList className="h-8 w-8 text-accent opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card border-border/50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Avaliadores</p>
                  <p className="text-3xl font-black">{stats?.total_evaluators || 0}</p>
                </div>
                <Shield className="h-8 w-8 text-green-500 opacity-50" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-card border-border/50">
            <CardContent className="p-4">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground">Média Geral</p>
                  <p className={`text-3xl font-black ${getScoreColor(stats?.average_overall || 0)}`}>
                    {stats?.average_overall || 0}
                  </p>
                </div>
                <TrendingUp className="h-8 w-8 text-yellow-500 opacity-50" />
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid gap-8 lg:grid-cols-2">
          {/* Skill Statistics Chart */}
          <Card className="bg-card border-border/50 shadow-xl">
            <CardHeader>
              <CardTitle className="text-xl font-bold tracking-tight flex items-center gap-2">
                <BarChart3 className="h-5 w-5 text-primary" />
                Estatísticas por Habilidade
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="w-full h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={skillChartData} layout="vertical">
                    <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                    <XAxis type="number" domain={CHART_DOMAIN} tick={CHART_TICK_STYLE} />
                    <YAxis type="category" dataKey="skill" tick={CHART_TICK_STYLE} width={80} />
                    <Tooltip 
                      contentStyle={CHART_TOOLTIP_STYLE}
                      labelStyle={CHART_LABEL_STYLE}
                    />
                    <Bar dataKey="média" fill="#007AFF" radius={BAR_RADIUS} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Radar Chart */}
          <Card className="bg-card border-border/50 shadow-xl">
            <CardHeader>
              <CardTitle className="text-xl font-bold tracking-tight">
                Radar de Habilidades Médias
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="w-full h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart data={radarData}>
                    <PolarGrid stroke="#27272A" />
                    <PolarAngleAxis dataKey="skill" tick={CHART_TICK_SMALL} />
                    <PolarRadiusAxis angle={30} domain={CHART_DOMAIN} tick={CHART_TICK_STYLE} />
                    <Radar
                      name="Média"
                      dataKey="value"
                      stroke="#007AFF"
                      fill="#007AFF"
                      fillOpacity={0.3}
                    />
                  </RadarChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Position Distribution */}
          <Card className="bg-card border-border/50 shadow-xl">
            <CardHeader>
              <CardTitle className="text-xl font-bold tracking-tight flex items-center gap-2">
                <Users className="h-5 w-5 text-primary" />
                Distribuição por Posição
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="w-full h-80">
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart>
                    <Pie
                      data={positionData}
                      cx="50%"
                      cy="50%"
                      innerRadius={60}
                      outerRadius={100}
                      paddingAngle={5}
                      dataKey="value"
                      label={({ name, value }) => `${name}: ${value}`}
                    >
                      {positionData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip 
                      contentStyle={CHART_TOOLTIP_STYLE}
                    />
                  </PieChart>
                </ResponsiveContainer>
              </div>
            </CardContent>
          </Card>

          {/* Top Players */}
          <Card className="bg-card border-border/50 shadow-xl">
            <CardHeader>
              <CardTitle className="text-xl font-bold tracking-tight flex items-center gap-2">
                <Trophy className="h-5 w-5 text-yellow-400" />
                Top 5 Jogadores
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {stats?.top_players?.map((player, index) => (
                  <div 
                    key={player.player_id}
                    className="flex items-center gap-3 p-3 rounded-lg bg-secondary/30"
                  >
                    <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center font-bold text-primary">
                      {index + 1}
                    </div>
                    {player.photo_url ? (
                      <img 
                        src={player.photo_url} 
                        alt={player.name}
                        className="w-10 h-10 rounded-full object-cover"
                      />
                    ) : (
                      <div className="w-10 h-10 rounded-full bg-card flex items-center justify-center">
                        <User className="h-5 w-5 text-muted-foreground" />
                      </div>
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="font-medium truncate">{player.name}</p>
                      <Badge variant="outline" className="text-xs">{player.position}</Badge>
                    </div>
                    <span className={`text-xl font-black ${getScoreColor(player.average)}`}>
                      {player.average}
                    </span>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Skill Leaders */}
        <Card className="bg-card border-border/50 shadow-xl mt-8">
          <CardHeader>
            <CardTitle className="text-xl font-bold tracking-tight flex items-center gap-2">
              <Zap className="h-5 w-5 text-yellow-400" />
              Líderes por Habilidade
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4">
              {stats?.skill_statistics?.map((skill, index) => (
                <div 
                  key={skill.skill}
                  className="p-4 rounded-lg bg-secondary/30 text-center"
                >
                  <p className="text-xs font-bold uppercase tracking-widest text-muted-foreground mb-2">
                    {skill.skill}
                  </p>
                  <p className={`text-2xl font-black ${getScoreColor(skill.average)}`}>
                    {skill.average}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1 truncate" title={skill.top_player}>
                    {skill.top_player || '-'}
                  </p>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Recent Evaluations */}
        <Card className="bg-card border-border/50 shadow-xl mt-8">
          <CardHeader>
            <CardTitle className="text-xl font-bold tracking-tight flex items-center gap-2">
              <Calendar className="h-5 w-5 text-primary" />
              Avaliações Recentes
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="border-border/50">
                    <TableHead className="text-xs font-bold uppercase tracking-widest">Data</TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-widest">Jogador</TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-widest">Avaliador</TableHead>
                    <TableHead className="text-xs font-bold uppercase tracking-widest text-center">Média</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {stats?.recent_evaluations?.map((eval_, index) => (
                    <TableRow key={eval_.id || index} className="border-border/50">
                      <TableCell className="text-muted-foreground">
                        {formatDate(eval_.created_at)}
                      </TableCell>
                      <TableCell className="font-medium">{eval_.player_name}</TableCell>
                      <TableCell>{eval_.evaluator_name}</TableCell>
                      <TableCell className={`text-center font-bold ${getScoreColor(eval_.overall)}`}>
                        {eval_.overall}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      </main>

      {/* Mobile Navigation */}
      <nav className="mobile-nav">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/dashboard')}
          className="flex flex-col items-center gap-1 h-auto py-2"
        >
          <Home className="h-5 w-5" />
          <span className="text-xs">Início</span>
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/statistics')}
          className="flex flex-col items-center gap-1 h-auto py-2 text-primary"
        >
          <BarChart3 className="h-5 w-5" />
          <span className="text-xs">Stats</span>
        </Button>
        <Button
          variant="ghost"
          size="sm"
          onClick={() => navigate('/evaluate')}
          className="flex flex-col items-center gap-1 h-auto py-2"
        >
          <Target className="h-5 w-5" />
          <span className="text-xs">Avaliar</span>
        </Button>
      </nav>
    </div>
  );
}
